import type { StaticOption, Step } from '@blooq/flow'
import { IonButton, IonIcon, IonSegment, IonSegmentButton, IonLabel } from '@ionic/react'
import {
  calendarOutline,
  callOutline,
  chatbubbleEllipsesOutline,
  checkmarkCircleOutline,
  chevronForwardOutline,
  closeCircleOutline,
  helpCircleOutline,
  pricetagsOutline,
  warningOutline,
} from 'ionicons/icons'
import { useMemo, useState } from 'react'

import { PERIODS, periodOf, splitDateLabel } from '../../lib/labels'

import type { DockProps } from './StepDock'

/*
 * A list of choices, drawn four different ways.
 *
 * Which way is decided from `source.kind` — the field the flow already sets to
 * say where the options came from — and never from the step's id. Dates want a
 * rail, times want a grid under a period switch, services want a price column,
 * and a plain menu wants tiles. Keying that off the data means a new step whose
 * options are dates gets the rail for free, and it keeps the LOOK decision here
 * while the ROUTING decision stays in canonical.ts.
 */

/**
 * Menu options carry stable value strings from the flow definition, so an icon
 * can be looked up from one. An unknown value falls through to a neutral glyph
 * rather than blanking the tile — a new menu item must never render an empty
 * square.
 */
export const OPTION_ICONS: Record<string, string> = {
  // The menu's six actions, as the front door's chips show them.
  catalog: pricetagsOutline,
  booking: calendarOutline,
  enquiry: chatbubbleEllipsesOutline,
  faq: helpCircleOutline,
  manage: checkmarkCircleOutline,
  call: callOutline,
  // Older static menus, and the manage view.
  schedule: calendarOutline,
  urgent: warningOutline,
  reschedule: calendarOutline,
  cancel: closeCircleOutline,
}

export function ChoiceDock({ view, busy, onSend }: DockProps) {
  const step = view.step as Extract<Step, { type: 'choice' }>
  const kind = step.source.kind
  const pick = (option: StaticOption) => onSend({ value: option.value }, option.label)

  if (view.options.length === 0) {
    /*
     * No options is a real answer, not an empty state.
     *
     * It happens when a business has nothing free in the window the flow
     * offers, and the honest thing is to say so and point at the phone — not to
     * leave a customer looking at a blank dock wondering whether it is still
     * loading.
     */
    return (
      <p className="blooq-empty">
        There are no times available right now. Please get in touch directly.
      </p>
    )
  }

  if (kind === 'availableDates') return <DateRail options={view.options} busy={busy} onPick={pick} />
  if (kind === 'availableSlots') return <TimeGrid options={view.options} busy={busy} onPick={pick} />

  if (kind === 'catalog') {
    return (
      <>
        {view.options.map((option) => (
          <ServiceRow key={option.value} option={option} busy={busy} onPick={pick} />
        ))}
      </>
    )
  }

  // A short list reads better as rows; four or more as tiles, which is what the
  // main menu always is.
  if (view.options.length > 3) return <TileGrid options={view.options} busy={busy} onPick={pick} />

  return (
    <>
      {view.options.map((option) => (
        <IonButton
          key={option.value}
          className="blooq-row"
          fill="outline"
          expand="block"
          disabled={busy}
          onClick={() => pick(option)}
        >
          <span style={{ marginInlineEnd: 'auto' }}>{option.label}</span>
          <IonIcon slot="end" icon={chevronForwardOutline} style={{ fontSize: 16 }} />
        </IonButton>
      ))}
    </>
  )
}

/**
 * A horizontal rail, not a calendar.
 *
 * The server returns only the dates this business is actually open and has room
 * on, so a month grid would be mostly disabled cells — the question already
 * answered, drawn as if it were still open. A rail scrolls with a thumb and
 * shows the answer.
 */
function DateRail({
  options,
  busy,
  onPick,
}: {
  options: StaticOption[]
  busy: boolean
  onPick: (option: StaticOption) => void
}) {
  return (
    <>
      <div className="blooq-label">Choose a day</div>
      <div className="blooq-rail" role="group" aria-label="Available dates">
        {options.map((option) => {
          const parts = splitDateLabel(option.label)
          return (
            <IonButton
              key={option.value}
              className="blooq-date"
              fill="outline"
              disabled={busy}
              aria-label={option.label}
              onClick={() => onPick(option)}
            >
              {parts ? (
                <span className="blooq-date__in">
                  <span className="blooq-date__wd">{parts.wd}</span>
                  <span className="blooq-date__d">{parts.d}</span>
                  <span className="blooq-date__mo">{parts.mo}</span>
                </span>
              ) : (
                <span style={{ fontSize: 12 }}>{option.label}</span>
              )}
            </IonButton>
          )
        })}
      </div>
    </>
  )
}

/**
 * A day is twenty-odd slots. Ungrouped that is a wall of pills.
 *
 * The web app stacks all three groups; here they are one segmented control and
 * one grid, because a phone dock is short and scrolling past Morning to reach
 * Evening on every booking is the kind of friction that only shows up on a
 * device. The segment is the platform's own control, so it also gets the
 * platform's own feel.
 */
function TimeGrid({
  options,
  busy,
  onPick,
}: {
  options: StaticOption[]
  busy: boolean
  onPick: (option: StaticOption) => void
}) {
  const groups = useMemo(() => {
    const buckets = new Map<string, StaticOption[]>()
    for (const option of options) {
      const period = periodOf(option.label)
      const existing = buckets.get(period)
      if (existing) existing.push(option)
      else buckets.set(period, [option])
    }
    return PERIODS.filter((period) => buckets.has(period)).map(
      (period) => [period, buckets.get(period)!] as const,
    )
  }, [options])

  // Opens on the first period that has anything in it, so a business whose day
  // starts at two in the afternoon does not open on an empty Morning.
  const [active, setActive] = useState<string>(groups[0]?.[0] ?? 'Morning')
  const shown = groups.find(([period]) => period === active) ?? groups[0]

  return (
    <>
      {groups.length > 1 ? (
        <IonSegment
          className="blooq-segment"
          value={shown?.[0]}
          onIonChange={(event) => setActive(String(event.detail.value ?? ''))}
        >
          {groups.map(([period]) => (
            <IonSegmentButton key={period} value={period}>
              <IonLabel>{period}</IonLabel>
            </IonSegmentButton>
          ))}
        </IonSegment>
      ) : null}

      <div className="blooq-times" role="group" aria-label={`${shown?.[0] ?? ''} slots`}>
        {(shown?.[1] ?? []).map((option) => (
          <IonButton
            key={option.value}
            className="blooq-time"
            fill="outline"
            disabled={busy}
            onClick={() => onPick(option)}
          >
            {option.label}
          </IonButton>
        ))}
      </div>
    </>
  )
}

/**
 * A service, with its price in a column of its own.
 *
 * Services arrive as "New Consultation · ₹800" — one string, because the flow
 * has no notion of a priced option and should not grow one for a renderer's
 * benefit. Splitting the fee out here gives it somewhere to sit instead of
 * trailing off the end of a sentence.
 */
function ServiceRow({
  option,
  busy,
  onPick,
}: {
  option: StaticOption
  busy: boolean
  onPick: (option: StaticOption) => void
}) {
  const separator = option.label.lastIndexOf(' · ')
  const name = separator === -1 ? option.label : option.label.slice(0, separator)
  const fee = separator === -1 ? null : option.label.slice(separator + 3)

  return (
    <IonButton
      className="blooq-row"
      fill="outline"
      expand="block"
      disabled={busy}
      aria-label={option.label}
      onClick={() => onPick(option)}
    >
      <span style={{ textAlign: 'left', whiteSpace: 'normal', marginInlineEnd: 'auto' }}>
        {name}
      </span>
      {fee ? (
        <span className="blooq-service__price">{fee}</span>
      ) : (
        <IonIcon slot="end" icon={chevronForwardOutline} style={{ fontSize: 16 }} />
      )}
    </IonButton>
  )
}

function TileGrid({
  options,
  busy,
  onPick,
}: {
  options: StaticOption[]
  busy: boolean
  onPick: (option: StaticOption) => void
}) {
  // An odd count would leave a hole in a two-column grid, so the last tile
  // spans — which also suits the menu, whose last item is usually the longest.
  const odd = options.length % 2 === 1

  return (
    <div className="blooq-tiles">
      {options.map((option, index) => (
        <IonButton
          key={option.value}
          className="blooq-tile"
          fill="outline"
          disabled={busy}
          style={odd && index === options.length - 1 ? { gridColumn: '1 / -1' } : undefined}
          onClick={() => onPick(option)}
        >
          <span className="blooq-tile__in">
            <span className="blooq-tile__icon" aria-hidden="true">
              <IonIcon icon={OPTION_ICONS[option.value] ?? chevronForwardOutline} />
            </span>
            <span className="blooq-tile__label">{option.label}</span>
          </span>
        </IonButton>
      ))}
    </div>
  )
}
